/**
 * usePartnerSettlementHandlers
 * 협력사 주정산내역 핸들러 훅
 */
import {useCallback} from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";

interface UsePartnerSettlementHandlersProps {
    filters: {
        acctStDate: string;
        acctEdDate: string;
        suppId: string;
    };
    setFilters: (filters: { acctStDate: string; acctEdDate: string; suppId: string }) => void;
    refreshSettlements: (acctStDate: string, acctEdDate: string, suppId?: string) => Promise<number>;
    downloadExcel: (acctStDate: string, acctEdDate: string, suppId?: string) => Promise<void>;
    resetPagination: () => void;
}

export const usePartnerSettlementHandlers = ({
    filters,
    refreshSettlements,
    downloadExcel,
    resetPagination
}: UsePartnerSettlementHandlersProps) => {
    const { showToast } = useToast();

    /**
     * 정산내역 검색
     * 정산시작일, 정산종료일 필수 검증 후 데이터 조회
     */
    const handleSearch = useCallback(async () => {
        if (!filters.acctStDate) {
            showToast('정산시작일을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        if (!filters.acctEdDate) {
            showToast('정산종료일을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        const resultCount = await refreshSettlements(
            filters.acctStDate,
            filters.acctEdDate,
            filters.suppId || undefined
        );
        resetPagination();

        // 조회 결과가 0건인 경우 Toast 메시지 표시
        if (resultCount === 0) {
            showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
        }
    }, [filters, refreshSettlements, resetPagination, showToast]);

    /**
     * 엑셀 다운로드
     * 현재 필터 조건으로 정산내역 엑셀 파일 다운로드
     */
    const handleExcelDownload = useCallback(async () => {
        if (!filters.acctStDate) {
            showToast('정산시작일을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        if (!filters.acctEdDate) {
            showToast('정산종료일을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        await downloadExcel(
            filters.acctStDate,
            filters.acctEdDate,
            filters.suppId || undefined
        );
    }, [filters, downloadExcel, showToast]);

    return {
        handleSearch,
        handleExcelDownload
    };
};
